import { useCallback, useMemo } from 'react';
import { useAllObjectsExtraFieldsQuery } from '../../__generated__/graphql';
import {
  allExtraFieldsSupportedObjects,
  ExtraFieldsFormIdsLookup,
  ExtraFieldsSupportedObject,
  ExtraFieldsTableId,
} from './types';
import { selectScopedFields } from './logic';

export const useObjectExtraFields = (
  objectApiName: ExtraFieldsSupportedObject | string,
) => {
  // we query all objects at once because it is cheap operation
  // include the passed objectApiName if it's not already in the supported list
  const objectApiNames = allExtraFieldsSupportedObjects.includes(
    objectApiName as ExtraFieldsSupportedObject,
  )
    ? allExtraFieldsSupportedObjects
    : [...allExtraFieldsSupportedObjects, objectApiName];

  const { data, loading, error } = useAllObjectsExtraFieldsQuery({
    variables: {
      objectApiNames,
    },
  });
  const objectExtraFields = useMemo(
    () =>
      data?.extraFields.objectsExtraFields.find(
        (objectExtraFields) =>
          objectExtraFields.objectApiName === objectApiName,
      ),
    [data, objectApiName],
  );

  const getFormFields = useCallback(
    (formId: ExtraFieldsFormIdsLookup) =>
      selectScopedFields(
        objectExtraFields?.form ?? [],
        typeof formId === 'string' ? [formId] : formId,
        (field) => field.formId,
      ),
    [objectExtraFields],
  );

  const getTableFields = useCallback(
    (tableId: ExtraFieldsTableId) =>
      selectScopedFields(
        objectExtraFields?.table ?? [],
        [tableId],
        (field) => field.tableId,
      ),
    [objectExtraFields],
  );
  return {
    rawObjectExtraFields: objectExtraFields,
    getFormFields,
    getTableFields,
    loading,
    error,
  };
};
